import { useEffect, useState } from 'react';
import type { StoreSettings } from '../types';
import { orderService } from '../services/api';

/**
 * Window event dispatched after an admin saves store settings, so any mounted
 * consumer (e.g. the free-shipping banner in the always-mounted Navigation)
 * refetches immediately instead of waiting for the next full page load.
 */
export const PRICING_CONFIG_UPDATED_EVENT = 'pricing-config-updated';

/**
 * Fetches the public storefront pricing config (shipping + tax rules) from the
 * backend, which reads it from the `store_settings` DB row — never hardcoded.
 * Returns `null` until the backend responds (or if the request fails), so
 * callers can hold off rendering anything pricing-dependent until then.
 */
export function usePricingConfig(): StoreSettings | null {
  const [pricing, setPricing] = useState<StoreSettings | null>(null);

  useEffect(() => {
    let active = true;
    const load = () => {
      orderService.getPricingConfig().then(p => { if (active) setPricing(p); }).catch(() => {});
    };
    load();
    window.addEventListener(PRICING_CONFIG_UPDATED_EVENT, load);
    return () => {
      active = false;
      window.removeEventListener(PRICING_CONFIG_UPDATED_EVENT, load);
    };
  }, []);

  return pricing;
}

/**
 * Whether the store currently accepts online orders (admin-toggleable store
 * setting). `null` while the config is still loading, so callers can pick a
 * safe default for that brief window (usually "not yet" — never flash a buy
 * button that might have to be taken away).
 */
export function useOnlineOrderingEnabled(): boolean | null {
  const pricing = usePricingConfig();
  return pricing ? pricing.onlineOrderingEnabled : null;
}
